// Session boot (GET /api/me) and the live event stream (GET /api/events) wired into the store.
import { api, ApiError, onAccountLocked } from "./api";
import { withMockAccess } from "./auth/authApi";
import { authReady, getAuth, loadAuthConfig, setAuth } from "./auth/state";
import { artifactFromJob, cardForJob, directorJobUpdate, scopedTitle, type UpdateScope } from "./chat";
import { director, playWaits } from "./director";
import { contextQueue } from "./playwait";
import { firePreviewEvent } from "./notes"; // v4 (T4)
import { jobMovesNext } from "./nextstep";
import { flickerDirector } from "./directors";
import { onChangeEvent } from "./changes";
import { bump, get, set, showArtifact, toast } from "./store";
import type { JobView, Project, StudioEvent } from "./types";

/** Shown when sign-in worked but the very next request has no session: the browser dropped the cookie. */
export const COOKIE_BLOCKED = "You're signed in, but your browser didn't keep the session. Check that cookies are allowed for this site.";

/**
 * Load the signed-in user. `afterLogin`: we just signed in or accepted an invite, so a 401 here means the session
 * cookie didn't stick; that throws COOKIE_BLOCKED instead of quietly showing the sign-in form again.
 */
// Wave 1 safety: any 403 ACCOUNT_LOCKED (a call, or /api/me from an api that refuses it too) shows the calm "account
// paused" page: the gate sees `locked`, the app (and its events) unmount and the Director stops.
onAccountLocked(() => {
  if (get().locked) return;
  set({ locked: true, auth: "in" });
  director.stop("signout");
});

export async function boot(opts: { afterLogin?: boolean } = {}) {
  // v2.1 (D): know the sign-in method first and, with Clerk, wait for clerk-js so /api/me carries its token
  await loadAuthConfig();
  await authReady();
  try {
    const me = withMockAccess(await api.me());
    set({ auth: "in", me, balance: me.balance, locked: !!me.user.safetyLocked });
    setAuth({ sessionError: null });
  } catch (e) {
    if (e instanceof ApiError && e.code === "ACCOUNT_LOCKED") return; // the listener above showed the paused page
    // signed in with Clerk but the api said no (unverified email, wrong authorized party...): the gate explains
    if (getAuth().clerk?.signedIn) {
      setAuth({ sessionError: (e as Error).message });
      set({ auth: "out", me: null });
      return;
    }
    if (e instanceof ApiError && e.status === 401) {
      set({ auth: "out", me: null });
      if (opts.afterLogin) throw new Error(COOKIE_BLOCKED);
    } else {
      // the API is down or unreachable: say so instead of bouncing to sign-in
      set({ auth: "out", me: null });
      throw e;
    }
  }
}

// scene order per project, so a job event for scene "s2" can say "Scene 2"
const sceneOrder = new Map<string, string[]>();
const aspects = new Map<string, string>();
const posters = new Map<string, string>(); // `${projectId}/${sceneId}` -> frame url, the poster of that scene's clip
const titles = new Map<string, string>(); // projectId -> title, so an update about another video can name it
export const projectAspect = (projectId: string) => aspects.get(projectId) ?? "9:16";
/** The poster for a clip or render card: the scene's frame (the first scene's for the render). */
export const framePoster = (projectId: string, sceneId?: string) => posters.get(`${projectId}/${sceneId ?? ""}`) ?? posters.get(`${projectId}/`);
export function rememberProject(p: Project) {
  titles.set(p.id, p.title);
  if (p.script) sceneOrder.set(p.id, p.script.scenes.map((s) => s.id));
  if (p.brief.aspect) aspects.set(p.id, p.brief.aspect);
  p.script?.scenes.forEach((s, i) => {
    const url = p.shots[s.id]?.frame?.url || s.imageUrl;
    if (!url) return;
    posters.set(`${p.id}/${s.id}`, url);
    if (i === 0) posters.set(`${p.id}/`, url);
  });
  // seed live jobs from the project view so a page opened mid-job shows progress straight away
  if (p.jobs.length)
    set((s) => {
      const jobs = { ...s.jobs };
      for (const j of p.jobs) if (!jobs[j.id] || jobs[j.id].updatedAt < j.updatedAt) jobs[j.id] = j;
      return { jobs };
    });
}
/** 2026-11-04: Home's list (every video): titles and scene order only, so job updates can name the video and scene. */
export function rememberTitles(ps: Pick<Project, "id" | "title" | "script">[]) {
  for (const p of ps) {
    titles.set(p.id, p.title);
    if (p.script) sceneOrder.set(p.id, p.script.scenes.map((s) => s.id));
  }
}

/** The video open now: the editing suite's, else the /p/:id route (its review and cast pages too); null elsewhere. */
export function openProjectId(): string | null {
  const suite = get().suiteProject;
  if (suite) return suite;
  if (typeof location === "undefined") return null;
  const m = /^\/p\/([^/]+)/.exec(location.pathname);
  return m ? decodeURIComponent(m[1]!) : null;
}
const scopeOf = (job: JobView): UpdateScope => ({ openProjectId: openProjectId(), projectTitle: job.projectId ? (titles.get(job.projectId) ?? null) : null });

export const sceneIndex = (projectId: string, sceneId: string | null | undefined) => {
  if (!sceneId) return undefined;
  const i = sceneOrder.get(projectId)?.indexOf(sceneId) ?? -1;
  return i >= 0 ? i + 1 : undefined;
};

/**
 * v4 (spec-v4 section 8.2, "SSE while waiting"): job updates wait while a play_and_wait is pending, so the Director
 * never announces a finished frame over a clip; they follow, in order, just after the tool result.
 */
const jobContext = contextQueue(
  () => !!playWaits.pending(),
  (t) => director.context(t),
);
playWaits.onSettle(() => setTimeout(() => jobContext.flush(), 600));

/** A job moved: track it, update (or add) its chat card, tell the Director, and refresh what it changed. */
export function onJob(job: JobView) {
  const prev = get().jobs[job.id];
  set((s) => ({ jobs: { ...s.jobs, [job.id]: job } }));
  // a job starting (a frame, a clip, the render): the Director's light flickers warm
  if (!prev && (job.status === "queued" || job.status === "running")) flickerDirector();
  const idx = job.projectId ? sceneIndex(job.projectId, job.sceneId) : undefined;

  // the chat card: update the one that tracks this job; finished work gets a card even if nothing showed it yet
  const scope = scopeOf(job);
  const made = artifactFromJob(job, idx);
  // 2026-11-04: another video's card says which video it is
  const card = made && { ...made, title: scopedTitle(made.title, job, scope) };
  if (card && (cardForJob(get().transcript, job.id) || job.status === "done" || job.status === "failed")) {
    const existing = cardForJob(get().transcript, job.id);
    // keep a card's own title (the Director's "Brian reads your hook") and actions it was given
    showArtifact(existing ? { ...card, title: existing.title, actions: card.status === "working" ? existing.actions : card.actions } : card);
  }

  // never silent: finish and fail always go to the Director, progress only at milestones; 2026-11-04 (Keaton's report):
  // only for the video open now in full; another video's gets one short line naming it, or nothing (chat.ts)
  const update = directorJobUpdate(prev, job, idx, scope);
  if (update) jobContext.push(update);

  // the final render also drives the v1 render panel
  if (job.kind === "render" && job.projectId) {
    const pid = job.projectId;
    const r = get().renders[pid];
    set((s) => ({
      renders: {
        ...s.renders,
        [pid]: { id: job.id, projectId: pid, status: job.status, step: job.step, progress: job.progress, error: job.error, videoAssetId: job.assetId, videoUrl: job.url ?? r?.videoUrl ?? null, credits: job.credits, createdAt: job.createdAt },
      },
    }));
  }
  // fix-produce: the NEXT bar follows every job that starts or moves (a started produce is "busy", a done one moves on)
  if (jobMovesNext(prev, job)) bump("next");
  if (job.status === "done" && prev?.status !== "done") bump("project", "projects", "library", ...(job.kind === "character" ? (["characters"] as const) : []));
  if (job.status === "failed" && prev?.status !== "failed") {
    if (job.kind === "character") bump("characters");
    toast(`${card?.title ?? (job.kind === "character" ? "The character sheet" : "That job")} didn't work: ${job.error ?? "unknown error"}. The credits came back.`, "bad");
    bump("project", "credits");
  }
}

function onEvent(e: StudioEvent) {
  switch (e.type) {
    case "credits":
      set({ balance: e.balance });
      bump("credits");
      break;
    case "job":
      onJob(e.job);
      break;
    case "render": {
      const prev = get().renders[e.render.projectId];
      set((s) => ({ renders: { ...s.renders, [e.render.projectId]: { ...prev, ...e.render, videoUrl: e.render.videoUrl ?? prev?.videoUrl } } }));
      if (prev?.status !== e.render.status) bump("next");
      if (e.render.status === "done" && prev?.status !== "done") {
        toast("Your ad is ready to watch.", "good");
        bump("projects", "project", "library");
      }
      if (e.render.status === "failed" && prev?.status !== "failed") {
        toast(`The render stopped: ${e.render.error ?? "unknown error"}. Your credits were refunded.`, "bad");
        bump("projects", "project", "credits");
      }
      break;
    }
    case "scene_image":
      set((s) => ({ sceneImages: { ...s.sceneImages, [e.projectId]: { ...s.sceneImages[e.projectId], [e.sceneId]: e.url } } }));
      break;
    case "asset":
      bump("library");
      break;
    case "preview":
      // v4 (T4): a range preview's progress, for the review screen
      firePreviewEvent(e.preview);
      if (e.preview.status === "done" || e.preview.status === "failed") bump("notes");
      break;
    case "project":
      // phase 2: also a new poster ("Change thumbnail"), which the library's cards show
      bump("projects", "project", "library");
      break;
    case "change": // v4 (V4-U1): offer Undo, refresh the History fold
      onChangeEvent(e.change);
      break;
  }
}

let stop: (() => void) | null = null;
export function startEvents() {
  stop?.();
  stop = api.events(onEvent, (live) => set({ live }));
}
export function stopEvents() {
  stop?.();
  stop = null;
}
